import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { CreditCard, Plus, Edit, Trash2, Check, RefreshCw, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncReport, setSyncReport] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      setPlans(response.data || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Pläne');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = () => {
    setEditingPlan({
      id: `plan_${Date.now()}`,
      name: '',
      price: 0,
      billing_interval: 'monthly',
      stripe_price_id: '',
      is_visible_on_website: true,
      limits: {
        sessions_per_month: 10,
        storage_gb: 5,
        max_uploads_per_session: 10
      },
      features: {
        basic_vehicle_database: true,
        pdf_reports: true,
        email_support: true,
        extended_vehicle_database: false,
        dual_ai_system: false,
        custom_knowledge_base: false,
        live_web_research: false,
        technical_specifications: false,
        repair_manuals_access: false,
        vehicle_data_library: false,
        vin_decoder: false,  // NEU: VIN Decoder Feature
        obd_live_diagnosis: false,  // NEU: OBD Live Diagnose
        diagnosis_feedback_system: false,
        priority_support: false,
        custom_branding: false,
        detailed_test_instructions: true,
        actual_value_assessment: false,
        repair_instructions: false,
        file_uploads: false
      }
    });
    setIsCreating(true);
  };

  const handleSavePlan = async () => {
    try {
      if (isCreating) {
        await axios.post(`${API}/admin/plans`, editingPlan);
        toast.success('Plan erfolgreich erstellt');
      } else {
        await axios.put(`${API}/admin/plans/${editingPlan.id}`, editingPlan);
        toast.success('Plan erfolgreich aktualisiert');
      }
      setEditingPlan(null);
      setIsCreating(false);
      fetchPlans();
    } catch (error) {
      toast.error('Fehler beim Speichern des Plans');
      console.error(error);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!confirm('Sind Sie sicher, dass Sie diesen Plan löschen möchten?')) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/plans/${planId}`);
      toast.success('Plan erfolgreich gelöscht');
      fetchPlans();
    } catch (error) {
      toast.error('Fehler beim Löschen des Plans');
      console.error(error);
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan({ ...plan });
    setIsCreating(false);
  };

  const handleCancelEdit = () => {
    setEditingPlan(null);
    setIsCreating(false);
  };

  const handleSyncToStripe = async () => {
    if (!confirm('Möchten Sie alle Pläne mit Stripe synchronisieren? Dies kann einige Sekunden dauern.')) {
      return;
    }
    
    setSyncing(true);
    setSyncReport(null);
    
    try {
      const response = await axios.post(`${API}/admin/plans/sync-stripe`);
      const report = response.data.report;
      
      setSyncReport(report);
      
      // Show summary toast
      const successCount = report.synced + report.created + report.updated;
      if (report.errors === 0) {
        toast.success(`Synchronisation erfolgreich! ${successCount} Pläne synchronisiert.`);
      } else {
        toast.warning(`Synchronisation abgeschlossen mit ${report.errors} Fehler(n).`);
      }
      
      // Refresh plans
      fetchPlans();
    } catch (error) {
      toast.error('Fehler bei der Stripe-Synchronisation');
      console.error(error);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <div className="flex items-center justify-center h-64">
          <p style={{ color: '#94A3B8' }}>Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
            Abo-Pläne verwalten
          </h2>
          <div className="flex gap-3">
            <Button
              onClick={handleSyncToStripe}
              disabled={syncing}
              variant="outline"
              style={{ 
                borderColor: '#1EA0E6', 
                color: syncing ? '#94A3B8' : '#1EA0E6',
                backgroundColor: 'transparent'
              }}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Synchronisiere...' : 'Mit Stripe synchronisieren'}
            </Button>
            <Button
              onClick={handleCreatePlan}
              style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Neuer Plan
            </Button>
          </div>
        </div>

        {/* Sync Report Card */}
        {syncReport && (
          <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1EA0E6' }}>
            <CardHeader>
              <CardTitle style={{ color: '#E6EDF3' }}>
                Stripe Synchronisations-Bericht
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 rounded" style={{ backgroundColor: '#0B0F14' }}>
                  <p style={{ color: '#94A3B8', fontSize: '14px' }}>Gesamt</p>
                  <p className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>{syncReport.total_plans}</p>
                </div>
                <div className="text-center p-4 rounded" style={{ backgroundColor: '#0B0F14' }}>
                  <p style={{ color: '#94A3B8', fontSize: '14px' }}>Synchronisiert</p>
                  <p className="text-2xl font-bold" style={{ color: '#22C55E' }}>{syncReport.synced}</p>
                </div>
                <div className="text-center p-4 rounded" style={{ backgroundColor: '#0B0F14' }}>
                  <p style={{ color: '#94A3B8', fontSize: '14px' }}>Erstellt</p>
                  <p className="text-2xl font-bold" style={{ color: '#3B82F6' }}>{syncReport.created}</p>
                </div>
                <div className="text-center p-4 rounded" style={{ backgroundColor: '#0B0F14' }}>
                  <p style={{ color: '#94A3B8', fontSize: '14px' }}>Aktualisiert</p>
                  <p className="text-2xl font-bold" style={{ color: '#F59E0B' }}>{syncReport.updated}</p>
                </div>
              </div>

              {syncReport.details && syncReport.details.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold mb-3" style={{ color: '#E6EDF3' }}>Details</h4>
                  {syncReport.details.map((detail, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-3 rounded"
                      style={{ backgroundColor: '#0B0F14' }}
                    >
                      <div className="flex items-center gap-3">
                        {detail.status === 'success' || detail.status === 'ok' ? (
                          <CheckCircle className="w-5 h-5" style={{ color: '#22C55E' }} />
                        ) : detail.status === 'failed' ? (
                          <XCircle className="w-5 h-5" style={{ color: '#EF4444' }} />
                        ) : (
                          <AlertCircle className="w-5 h-5" style={{ color: '#F59E0B' }} />
                        )}
                        <div>
                          <p style={{ color: '#E6EDF3', fontWeight: '500' }}>{detail.plan}</p>
                          <p style={{ color: '#94A3B8', fontSize: '14px' }}>
                            {detail.action === 'created' && 'Neu in Stripe erstellt'}
                            {detail.action === 'updated' && 'In Stripe aktualisiert'}
                            {detail.action === 'validated' && 'Bereits synchron'}
                            {detail.action === 'recreated' && 'In Stripe neu erstellt'}
                            {detail.action === 'error' && `Fehler: ${detail.error}`}
                          </p>
                        </div>
                      </div>
                      {detail.stripe_price_id && (
                        <code 
                          className="px-2 py-1 rounded text-xs"
                          style={{ backgroundColor: '#1F2937', color: '#7CD4FD' }}
                        >
                          {detail.stripe_price_id}
                        </code>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <Button
                  onClick={() => setSyncReport(null)}
                  variant="outline"
                  style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
                >
                  Schließen
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {editingPlan && (
          <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1EA0E6' }}>
            <CardHeader>
              <CardTitle style={{ color: '#E6EDF3' }}>
                {isCreating ? 'Neuen Plan erstellen' : 'Plan bearbeiten'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block mb-2" style={{ color: '#94A3B8' }}>Plan-Name</label>
                  <Input
                    value={editingPlan.name}
                    onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                    placeholder="z.B. Basic, Pro, Enterprise"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>

                <div>
                  <label className="block mb-2" style={{ color: '#94A3B8' }}>Preis (€)</label>
                  <Input
                    type="number"
                    value={editingPlan.price}
                    onChange={(e) => setEditingPlan({ ...editingPlan, price: parseFloat(e.target.value) })}
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>

                <div>
                  <label className="block mb-2" style={{ color: '#94A3B8' }}>Intervall</label>
                  <select
                    value={editingPlan.billing_interval}
                    onChange={(e) => setEditingPlan({ ...editingPlan, billing_interval: e.target.value })}
                    className="w-full p-2 rounded border"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  >
                    <option value="monthly">Monatlich</option>
                    <option value="yearly">Jährlich</option>
                    <option value="lifetime">Einmalig</option>
                  </select>
                </div>

                <div>
                  <label className="block mb-2" style={{ color: '#94A3B8' }}>Diagnosen pro Monat</label>
                  <Input
                    type="number"
                    value={editingPlan.limits?.sessions_per_month || 0}
                    onChange={(e) => setEditingPlan({
                      ...editingPlan,
                      limits: { ...editingPlan.limits, sessions_per_month: parseInt(e.target.value) }
                    })}
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
              </div>

              {/* Website-Sichtbarkeit Checkbox */}
              <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: '#0B0F14', border: '1px solid #1F2937' }}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingPlan.is_visible_on_website !== false}
                    onChange={(e) => setEditingPlan({ ...editingPlan, is_visible_on_website: e.target.checked })}
                    className="w-5 h-5 rounded"
                  />
                  <div>
                    <span className="font-semibold" style={{ color: '#E6EDF3' }}>Plan auf Website sichtbar</span>
                    <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
                      Wenn deaktiviert, ist der Plan für Kunden auf der Website und im Checkout nicht sichtbar. 
                      Admins können den Plan weiterhin zuweisen und nutzen.
                    </p>
                  </div>
                </label>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold mb-3" style={{ color: '#E6EDF3' }}>Features</h4>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    { key: 'basic_vehicle_database', label: 'Basis Fahrzeugdatenbank' },
                    { key: 'pdf_reports', label: '📄 PDF Reports exportieren' },
                    { key: 'email_support', label: '📧 Email Support' },
                    { key: 'extended_vehicle_database', label: '🚗 Erweiterte Fahrzeugdatenbank' },
                    { key: 'dual_ai_system', label: '🤖 Duales KI System (Gemini + GPT-4o)' },
                    { key: 'custom_knowledge_base', label: '📚 RAG: Eigene Dokumente hochladen (PDFs für Diagnose)', highlight: true, description: 'Admin kann eigene Werkstatthandbücher/PDFs hochladen' },
                    { key: 'live_web_research', label: '🌐 Live Web-Recherche (Tavily)' },
                    { key: 'technical_specifications', label: '🔧 Tech-Daten in Diagnose (Öl, Drehmomente, Sensoren)', highlight: true, description: 'AI nutzt gesammelte Fahrzeugdaten in Diagnose-Anweisungen' },
                    { key: 'repair_manuals_access', label: '🛠️ Reparaturanleitungen-Zugriff' },
                    { key: 'vehicle_data_library', label: '📖 Fahrzeugdaten-Bibliothek (Benutzer-Zugriff)', highlight: true, description: 'Benutzer können technische Daten für 1000+ Fahrzeuge durchsuchen' },
                    { key: 'vin_decoder', label: '🔢 VIN Decoder', highlight: true, description: 'Fahrzeug-Identifizierung über Fahrgestellnummer - extrahiert Hersteller, Modell, Baujahr, Motor etc.' },
                    { key: 'obd_live_diagnosis', label: '📡 OBD Live-Diagnose', highlight: true, description: 'Echtzeit-Diagnose über OBD-Adapter mit Live-Daten' },
                    { key: 'diagnosis_feedback_system', label: '⭐ Diagnose-Feedback-System' },
                    { key: 'priority_support', label: '🚀 Prioritäts-Support' },
                    { key: 'custom_branding', label: '🎨 Custom Branding' },
                    { key: 'detailed_test_instructions', label: '📋 Detaillierte Prüfanweisungen' },
                    { key: 'actual_value_assessment', label: '📊 Istwert-Beurteilung' },
                    { key: 'repair_instructions', label: '🔨 Reparatur-Anleitungen' },
                    { key: 'file_uploads', label: '📎 Datei-Uploads (Bilder/Videos/Audio)' }
                  ].map(feature => (
                    <label
                      key={feature.key}
                      className="flex items-start gap-2 p-3 rounded cursor-pointer hover:bg-opacity-80 transition-all"
                      style={{ 
                        backgroundColor: feature.highlight ? '#1EA0E610' : '#0B0F14', 
                        borderColor: feature.highlight ? '#1EA0E6' : '#1F2937', 
                        border: '1px solid' 
                      }}
                      title={feature.description || feature.label}
                    >
                      <input
                        type="checkbox"
                        checked={editingPlan.features?.[feature.key] || false}
                        onChange={(e) => setEditingPlan({
                          ...editingPlan,
                          features: { ...editingPlan.features, [feature.key]: e.target.checked }
                        })}
                        className="w-4 h-4 mt-1"
                      />
                      <div className="flex-1">
                        <span style={{ color: '#E6EDF3' }}>{feature.label}</span>
                        {feature.description && (
                          <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                            {feature.description}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <Button
                  onClick={handleCancelEdit}
                  variant="outline"
                  style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleSavePlan}
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Speichern
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan, idx) => (
            <Card
              key={plan.id}
              style={{
                backgroundColor: '#0F141A',
                borderColor: idx === 1 ? '#1EA0E6' : '#1F2937',
                borderWidth: idx === 1 ? '2px' : '1px'
              }}
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <CardTitle style={{ color: '#E6EDF3' }}>{plan.name}</CardTitle>
                    {plan.is_visible_on_website === false && (
                      <span
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{ backgroundColor: '#F59E0B20', color: '#F59E0B', border: '1px solid #F59E0B50' }}
                        title="Dieser Plan ist für Kunden nicht sichtbar"
                      >
                        Versteckt
                      </span>
                    )}
                  </div>
                  {idx === 1 && (
                    <span
                      className="px-2 py-1 rounded text-xs font-bold"
                      style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                    >
                      BELIEBT
                    </span>
                  )}
                </div>
                <div className="mt-4">
                  <span className="text-3xl font-bold" style={{ color: '#7CD4FD' }}>
                    €{plan.price}
                  </span>
                  <span style={{ color: '#94A3B8' }}>
                    {plan.billing_interval === 'monthly' ? '/Monat' : plan.billing_interval === 'yearly' ? '/Jahr' : ' einmalig'}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6">
                  <p style={{ color: '#E6EDF3' }}>
                    <strong>{plan.limits?.sessions_per_month}</strong> Diagnosen/Monat
                  </p>
                  {plan.features?.basic_vehicle_database && (
                    <div className="flex items-center gap-2" style={{ color: '#22C55E' }}>
                      <Check className="w-4 h-4" />
                      <span>Basis Fahrzeugdatenbank</span>
                    </div>
                  )}
                  {plan.features?.pdf_reports && (
                    <div className="flex items-center gap-2" style={{ color: '#22C55E' }}>
                      <Check className="w-4 h-4" />
                      <span>PDF Reports</span>
                    </div>
                  )}
                  {plan.features?.email_support && (
                    <div className="flex items-center gap-2" style={{ color: '#22C55E' }}>
                      <Check className="w-4 h-4" />
                      <span>Email Support</span>
                    </div>
                  )}
                  {plan.features?.extended_vehicle_database && (
                    <div className="flex items-center gap-2" style={{ color: '#22C55E' }}>
                      <Check className="w-4 h-4" />
                      <span>Erweiterte Fahrzeugdatenbank</span>
                    </div>
                  )}
                  {plan.features?.dual_ai_system && (
                    <div className="flex items-center gap-2" style={{ color: '#22C55E' }}>
                      <Check className="w-4 h-4" />
                      <span>Duales KI System</span>
                    </div>
                  )}
                  {plan.features?.custom_knowledge_base && (
                    <div className="flex items-center gap-2" style={{ color: '#22C55E' }}>
                      <Check className="w-4 h-4" />
                      <span>Custom Knowledge Base</span>
                    </div>
                  )}
                  {plan.features?.live_web_research && (
                    <div className="flex items-center gap-2" style={{ color: '#22C55E' }}>
                      <Check className="w-4 h-4" />
                      <span>Live Web-Recherche</span>
                    </div>
                  )}
                  {plan.features?.priority_support && (
                    <div className="flex items-center gap-2" style={{ color: '#22C55E' }}>
                      <Check className="w-4 h-4" />
                      <span>Prioritäts-Support</span>
                    </div>
                  )}
                  {plan.features?.custom_branding && (
                    <div className="flex items-center gap-2" style={{ color: '#22C55E' }}>
                      <Check className="w-4 h-4" />
                      <span>Custom Branding</span>
                    </div>
                  )}
                  {plan.features?.detailed_test_instructions && (
                    <div className="flex items-center gap-2" style={{ color: '#22C55E' }}>
                      <Check className="w-4 h-4" />
                      <span>Detaillierte Prüfanweisungen</span>
                    </div>
                  )}
                  {plan.features?.actual_value_assessment && (
                    <div className="flex items-center gap-2" style={{ color: '#22C55E' }}>
                      <Check className="w-4 h-4" />
                      <span>Istwert-Beurteilung</span>
                    </div>
                  )}
                  {plan.features?.repair_instructions && (
                    <div className="flex items-center gap-2" style={{ color: '#22C55E' }}>
                      <Check className="w-4 h-4" />
                      <span>Reparatur-Anleitungen</span>
                    </div>
                  )}
                  {plan.features?.file_uploads && (
                    <div className="flex items-center gap-2" style={{ color: '#22C55E' }}>
                      <Check className="w-4 h-4" />
                      <span>Datei-Uploads</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleEditPlan(plan)}
                    variant="outline"
                    className="flex-1"
                    style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Bearbeiten
                  </Button>
                  <Button
                    onClick={() => handleDeletePlan(plan.id)}
                    variant="outline"
                    style={{ borderColor: '#EF4444', color: '#EF4444' }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
